import { SkinnedOwnerStageElement } from "./skinned-owner-stage.js";
export const AGENTS_STAGE_TAG = "node-agents-stage";
/**
 * Owner stage: agents — Coming soon (epic #1507 fidelity, stage 1/5). `agents.list.v1` is a
 * real, registered live query (`client/domain/src/queries/revisioned.ts`'s
 * `createAgentListQuery`, wired in `system/pwa/src/kernel-runtime.ts`) but unlike
 * `home.summary.v1`/`peers.directory.v1`/`wallet.balance.v1` it has no concrete domain type —
 * it's a generic revisioned-JSON mirror (`RevisionEntity<TValue>` with `TValue extends
 * JsonValue`, opaque at the type level). There is no confirmed field shape (no
 * `AgentSummary`/`AgentEntity` interface anywhere in `client/domain/src`) to bind into the
 * original chat UI without guessing field names, which the data policy treats the same as
 * fabrication. `query` is therefore `undefined`, and `SkinnedOwnerStageElement`'s
 * `query === undefined` path is bypassed entirely (this stage renders its own static chrome
 * directly from `connectedCallback`, matching the pre-existing `#1539`-scoped stub's
 * approach) — the chrome below has no data dependency of its own.
 *
 * This re-clothes the ORIGINAL skin's DOM skeleton for the agents chat shell (ported from
 * `SKIN/stages/default/agents/view/view.js` + `behavior/sidebar-extra.js:40-58`, verified real
 * via explicit-file grep against the vendored `client/ui/src/skin/stages-css/agents.css` and
 * `client/ui/src/skin/css/fixes.css`/`edits.css`, which carry the `.cg-run*`/`.cg-sched*`/
 * `.cg-actionbar`/`.cg-collapsible` rules `agents.css` itself doesn't). DOM order is kept
 * identical to the source (the vendored CSS flips the sidebar to the right via a `1fr auto`
 * grid) — do not reorder `.cg-sidebar` before `.cg-main`.
 *
 * What's ported verbatim (structure, not data):
 * - `.cg-sidebar`: `.cg-create-agent` + `.cg-sb-search` (both disabled — no create/search
 *   capability wired); the "Recently run"/"Scheduled" `.cg-sb-section`s pre-rendered in their
 *   `sidebar-extra.js`-driven END state (`.cg-sb-section--toggle.is-collapsed` + a static,
 *   unrotated `.cg-sb-sec-car` caret; the matching `.cg-runlist`/`.cg-schedlist` carry
 *   `.cg-collapsible.is-collapsed` to match) — this stage doesn't port the click-to-expand
 *   *behavior*, only the chrome's default (collapsed) *state*, so a plain screenshot matches
 *   the original's own un-interacted default.
 * - The original's 3 populated `.cg-run` rows (Neo/Atlas/Iris, with invented task summaries)
 *   and 3 `.cg-sched` rows are fabricated named-agent demo content with no backing query —
 *   replaced with a single empty-state row per list (`.cg-run`/`.cg-sched` shape, blank
 *   `.cg-run-ava`/`.cg-sched-ava` placeholder, muted "No runs yet" / "Nothing scheduled" text,
 *   no invented names).
 * - `.cg-actionbar` (`sidebar-extra.js:40-58`) ported VERBATIM structurally — label + 3
 *   colour-coded `.cg-ab-btn`s (coral Stop / green Double check / indigo tl;dr, all
 *   `disabled`) — positioned immediately before `.cg-sb-user`, matching the behavior script's
 *   `insertBefore(bar, user)`. The Stop button's `.cg-ab-dot` "task is actively running"
 *   indicator is omitted (there is no live task to indicate); the header's `.cg-ab-live` dot
 *   is kept (it's decorative panel chrome, not a claim about specific live data).
 * - `.cg-sb-user`: static "You" placeholder (avatar + name) rather than the original's
 *   fabricated "thanish" account name. A real per-session identity IS already wired
 *   elsewhere (`shell/current-device.ts`'s `loadCurrentDevice`/`currentDeviceLabel`, used by
 *   `topbar.ts`/`pmenu.ts`), but that's a *device* pairing name, not necessarily the agent
 *   chat account identity the original's `.cg-sb-user` represents — wiring it here is
 *   `#1539`'s job, not this DOM-only pass's.
 * - `.cg-main`: `.conversation > .thread` with a single empty-state `.msg.ai` turn ("Agent" /
 *   "No conversation yet — agent chat arrives with live data.") instead of the original's 5
 *   fake conversation turns (weather/calendar/portfolio/code-gen demo cards); `.composer-wrap
 *   > .composer` ports the textarea + `.composer-bar` (`.left`: plus / model button (static
 *   label, no `.cg-model-menu`) / attach / model pill (static "Choose model", no
 *   `.model-popover`) / voice — `.right`: send), all disabled. The original's 75-model
 *   popover and agent-card grid are not ported (fabricated catalog with no backing query).
 */
export class AgentsStageElement extends SkinnedOwnerStageElement {
    query = undefined;
    emptyMessage = "Agent chat isn't wired to live data yet.";
    connectedCallback() {
        super.connectedCallback();
        this.renderView(this, []);
    }
    renderView(root, _rows) {
        root.innerHTML = `
      <aside class="cg-sidebar" data-agents-sidebar>
        <button class="cg-create-agent" type="button" disabled title="Not available yet">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M8 3 V13 M3 8 H13"/></svg>
          Create agent
        </button>
        <div class="cg-sb-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
          <input type="text" placeholder="Search" aria-label="Search agents" disabled />
        </div>

        <div class="cg-sb-section cg-sb-section--toggle is-collapsed" data-agents-section="runs">
          Recently run
          <span class="cg-sb-sec-car" aria-hidden="true"><svg viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 2 L6.5 5 L3.5 8"/></svg></span>
        </div>
        <div class="cg-runlist cg-collapsible is-collapsed" data-agents-runlist>
          <div class="cg-run cg-run-shaped" data-agents-run-empty>
            <div class="cg-run-hd">
              <span class="cg-run-ava" aria-hidden="true"></span>
              <span class="cg-run-meta">
                <span class="cg-run-did">No runs yet</span>
              </span>
              <span class="cg-run-car" aria-hidden="true"></span>
            </div>
          </div>
        </div>

        <div class="cg-sb-section cg-sb-section--toggle is-collapsed" data-agents-section="scheduled">
          Scheduled
          <span class="cg-sb-sec-car" aria-hidden="true"><svg viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 2 L6.5 5 L3.5 8"/></svg></span>
        </div>
        <div class="cg-schedlist cg-collapsible is-collapsed" data-agents-schedlist>
          <div class="cg-sched cg-sched-shaped" data-agents-schedule-empty>
            <span class="cg-sched-ava" aria-hidden="true"></span>
            <span class="cg-sched-id">
              <span>Nothing scheduled</span>
            </span>
          </div>
        </div>

        <div class="cg-actionbar" data-agents-actionbar>
          <div class="cg-ab-label"><span class="cg-ab-live" aria-hidden="true"></span>Current actions</div>
          <div class="cg-ab-btns">
            <button class="cg-ab-btn cg-ab-stop" type="button" data-act="stop" disabled title="Not available yet">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="6.5" y="6.5" width="11" height="11" rx="2.4"/></svg>
              <span>Stop</span>
            </button>
            <button class="cg-ab-btn" type="button" data-act="double-check" disabled title="Not available yet">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12.5 L7 16.5 L13.5 8"/><path d="M11 14.5 L13 16.5 L21 7"/></svg>
              <span>Double check</span>
            </button>
            <button class="cg-ab-btn" type="button" data-act="tldr" disabled title="Not available yet">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7 H20 M4 12 H20 M4 17 H13"/></svg>
              <span>tl;dr</span>
            </button>
          </div>
        </div>

        <div class="cg-sb-user" data-agents-user>
          <span class="cg-sb-avatar">You</span>
          <span class="cg-sb-uname" data-agents-username>You</span>
        </div>
      </aside>
      <div class="cg-main" data-agents-main>
        <div class="conversation" data-agents-conversation>
          <div class="thread" data-agents-thread>
            <div class="msg ai fade" data-agents-empty-turn>
              <div class="turn-label"><span>Agent</span></div>
              <div class="response"><p>No conversation yet — agent chat arrives with live data.</p></div>
            </div>
          </div>
        </div>
        <div class="composer-wrap">
          <div class="composer">
            <textarea placeholder="Ask anything" rows="1" disabled></textarea>
            <div class="composer-bar">
              <div class="left">
                <button class="cg-plus" type="button" disabled title="Not available yet" aria-label="Add">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>
                </button>
                <div class="cg-model-wrap">
                  <button class="cg-model" type="button" disabled title="Not available yet">
                    <svg class="cg-model-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2 4 6v6c0 5 3.5 8 8 8s8-3 8-8V6z"/><path d="M9.5 12l1.8 1.8 3.5-3.6"/></svg>
                    <span>Model</span>
                    <svg class="cg-model-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
                  </button>
                </div>
                <button class="tool-btn" type="button" disabled title="Not available yet" aria-label="Attach file">
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10 4.5 L5 9.5 Q4 10.5 5 11.5 Q6 12.5 7 11.5 L12 6.5 Q13.5 5 12 3.5 Q10.5 2 9 3.5 L4 8.5 Q1.5 11 4 13.5 Q6.5 16 9 13.5"/></svg>
                </button>
                <div class="model-pill-wrap">
                  <button class="model-pill" type="button" disabled title="Not available yet">
                    <span>Choose model</span>
                    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"><path d="M3 5 L6 8 L9 5"/></svg>
                  </button>
                </div>
                <button class="tool-btn" type="button" disabled title="Not available yet" aria-label="Voice input">
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="6" y="1.5" width="4" height="8.5" rx="2"/><path d="M3.5 8.5 Q3.5 12 8 12 Q12.5 12 12.5 8.5"/><path d="M8 12 V14.5"/><path d="M6 14.5 H10"/></svg>
                </button>
              </div>
              <div class="right">
                <button class="send" type="button" disabled title="Not available yet" aria-label="Send">
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8 L13 8 M9 4 L13 8 L9 12"/></svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
    }
}
export function registerAgentsStageElement() {
    if (!customElements.get(AGENTS_STAGE_TAG)) {
        customElements.define(AGENTS_STAGE_TAG, AgentsStageElement);
    }
}
//# sourceMappingURL=agents-stage.js.map